import React, {memo, useCallback, useEffect, useMemo, useState} from "react";
import {Layout, Breadcrumb, Row, Input, Button, message, Select, Space, Upload, Table, Popconfirm} from 'antd';
import {useParams, Link} from 'react-router-dom';
import Api from "../api";
import eeIcon from "../assets/icons/et.png";
import enIcon from "../assets/icons/en.png";
import lvIcon from "../assets/icons/lv.png";
import {
    CheckCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    StarOutlined,
    UploadOutlined
} from '@ant-design/icons';
import axios from "axios";
import NewTranslation from "../components/new-translation";
import TranslationFormModal from "../components/translation-form-modal";
import {getCookie} from "../helpers";

const {Content} = Layout;
const {Search} = Input;
const {Option} = Select;

const TABLE_HEADER_OFFSET = 320;

const TranslationTextPreview = memo(({
    value,
    edited,
    placeholder = 'Click to edit',
    readonly = false,
    onClick,
}) => (
    <div
        className={`translation-cell${readonly ? ' translation-cell--readonly' : ''}`}
        onClick={onClick}
        title={value || placeholder}
    >
        {edited && <CheckCircleOutlined className="translation-cell__edited"/>}
        <span className="translation-cell__text">{value || placeholder}</span>
        {!readonly && <EditOutlined className="translation-cell__edit"/>}
    </div>
));

const TranslationInput = memo(({translationKey, initialValue, edited, masterValue, onUpdate}) => {
    const [value, setValue] = useState(initialValue ?? '');
    const [modalOpen, setModalOpen] = useState(false);
    const [draft, setDraft] = useState('');

    useEffect(() => {
        setValue(initialValue ?? '');
    }, [initialValue, translationKey]);

    const openEditor = () => {
        setDraft(value);
        setModalOpen(true);
    };

    const closeEditor = () => {
        setModalOpen(false);
    };

    const applyDraft = () => {
        setValue(draft);
        if (draft !== initialValue) {
            onUpdate(translationKey, draft);
        }
        closeEditor();
    };

    return (
        <>
            <TranslationTextPreview
                value={value}
                edited={edited}
                onClick={openEditor}
            />

            <TranslationFormModal
                open={modalOpen}
                mode="edit"
                translationKey={translationKey}
                draftValue={draft}
                masterValue={masterValue}
                onValueChange={setDraft}
                onSave={applyDraft}
                onClose={closeEditor}
            />
        </>
    );
});

const MasterTranslationPreview = memo(({translationKey, value}) => {
    const [modalOpen, setModalOpen] = useState(false);

    if (!value) {
        return <span className="translation-cell__empty">—</span>;
    }

    return (
        <>
            <TranslationTextPreview
                value={value}
                readonly
                placeholder="View master text"
                onClick={() => setModalOpen(true)}
            />

            <TranslationFormModal
                open={modalOpen}
                mode="view"
                translationKey={translationKey}
                draftValue={value}
                onValueChange={() => {}}
                onSave={() => setModalOpen(false)}
                onClose={() => setModalOpen(false)}
            />
        </>
    );
});

const TranslationView = () => {
    const [translations, setTranslations] = useState({});
    const [masterTranslation, setMasterTranslation] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingAI, setLoadingAi] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [updatedKeys, setUpdatedKeys] = useState(() => new Set());
    const [filter, setFilter] = useState('all');
    const [tableHeight, setTableHeight] = useState(600);
    const params = useParams();
    const isMasterLanguage = params.id === 'et';

    useEffect(() => {
        const updateTableHeight = () => {
            setTableHeight(Math.max(window.innerHeight - TABLE_HEADER_OFFSET, 320));
        };

        updateTableHeight();
        window.addEventListener('resize', updateTableHeight);

        return () => window.removeEventListener('resize', updateTableHeight);
    }, []);

    useEffect(() => {
        setLoading(true);

        Api.get('/admin/translations?language=' + params.id).then(res => {
            if (res.data.data.length > 0) {
                setTranslations(res.data.data[0].translations || {});
            } else {
                setTranslations({});
            }

            if (!isMasterLanguage) {
                Api.get('/admin/translations?language=et').then(masterRes => {
                    setMasterTranslation(masterRes.data.data[0]?.translations || {});
                });
            } else {
                setMasterTranslation({});
            }
        }).finally(() => {
            setLoading(false);
        });
    }, [params.id, isMasterLanguage]);

    const markUpdated = useCallback((key) => {
        setUpdatedKeys(prev => {
            if (prev.has(key)) {
                return prev;
            }

            const next = new Set(prev);
            next.add(key);
            return next;
        });
    }, []);

    const updateTranslation = useCallback((key, value) => {
        markUpdated(key);
        setTranslations(prev => ({
            ...prev,
            [key]: value,
        }));
    }, [markUpdated]);

    const saveTranslations = () => {
        const data = {
            language: params.id,
            translations: translations,
        };

        const apiDomains = ['https://api.fitlap.ee', 'https://api.fitlap.com', 'https://api.fitlap.in', 'https://api2.fitlap.fi', 'http://127.0.0.1:8000'];
        const token = getCookie('fl_admin_access_token');

        apiDomains.forEach(domain => {
            axios({
                method: "post",
                url: "/admin/translations",
                baseURL: domain,
                data: data,
                headers: {
                    'X-Auth-Token': `4sC6KWJisLkOkFQCW5xU`,
                    'Authorization': `Bearer ${token}`
                },
            }).then(() => {
                message.success('Translations synced: ' + domain);
            });
        });
    };

    const removeTranslation = (key) => {
        const apiDomains = ['https://api.fitlap.ee', 'https://api.fitlap.com', 'https://api.fitlap.in', 'http://127.0.0.1:8000'];

        apiDomains.forEach(domain => {
            axios({
                method: "delete",
                url: "/admin/translations",
                baseURL: domain,
                data: {
                    key: key,
                    language: params.id
                },
                headers: {
                    'X-Auth-Token': `4sC6KWJisLkOkFQCW5xU`
                },
            }).then(res => {
                message.success('Translations deleted: ' + domain);
                setTranslations(res.data.data.translations || {});
                setUpdatedKeys(prev => {
                    const next = new Set(prev);
                    next.delete(key);
                    return next;
                });
            });
        });
    };

    const onSearch = (value) => {
        setKeyword(value);
    };

    const handleUpload = (file) => {
        const reader = new FileReader();

        reader.onload = (event) => {
            try {
                const newTranslations = JSON.parse(event.target.result);

                setTranslations((prevTranslations) => {
                    const updatedTranslations = {...prevTranslations};
                    const newKeys = Object.keys(newTranslations).filter(
                        (key) => !Object.prototype.hasOwnProperty.call(prevTranslations, key)
                    );

                    newKeys.forEach((key) => {
                        updatedTranslations[key] = newTranslations[key];
                    });

                    message.success(`${newKeys.length} new keys added!`);

                    return updatedTranslations;
                });
            } catch (error) {
                message.error("Invalid JSON file.");
            }
        };

        reader.readAsText(file);
        return false;
    };

    const filteredRows = useMemo(() => {
        const entries = Object.entries(translations);
        const lowerKeyword = keyword.trim().toLowerCase();

        return entries.filter(([key, value]) => {
            if (filter === 'empty' && value && !updatedKeys.has(key)) {
                return false;
            }

            if (filter === 'edited' && !updatedKeys.has(key)) {
                return false;
            }

            if (!lowerKeyword) {
                return true;
            }

            const keyMatch = key.toLowerCase().includes(lowerKeyword);
            const valueMatch = value && String(value).toLowerCase().includes(lowerKeyword);
            const masterValue = masterTranslation[key];
            const masterMatch = !isMasterLanguage && masterValue &&
                String(masterValue).toLowerCase().includes(lowerKeyword);

            return keyMatch || valueMatch || masterMatch;
        }).map(([key, value]) => ({
            key,
            value: value ?? '',
        }));
    }, [translations, keyword, filter, updatedKeys, masterTranslation, isMasterLanguage]);

    const getImg = () => {
        if (params.id === 'et') {
            return eeIcon;
        }

        if (params.id === 'lv') {
            return lvIcon;
        }

        if (params.id === 'en') {
            return enIcon;
        }

        return "";
    };

    const translateUntranslated = async () => {
        setLoadingAi(true);

        const untranslatedKeys = Object.keys(masterTranslation).filter(key => !translations[key]);
        const untranslatedValues = untranslatedKeys.map(key => masterTranslation[key]);
        const limitedUntranslatedValues = untranslatedValues.slice(0, 20);
        const limitedUntranslatedKeys = untranslatedKeys.slice(0, 20);

        const OpenAIApiKey = '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';
        const targetLanguage = params.id === 'fi' ? "Finnish" : "English";

        const messagesContent = `
        Please translate the following texts to ${targetLanguage} and return the result as a JSON object with the original text as keys and the translated text as values:
        ${limitedUntranslatedValues.map(value => `"${value}"`).join(', ')}
    `;

        try {
            const response = await axios.post('https://api.openai.com/v1/chat/completions', {
                model: "gpt-3.5-turbo",
                messages: [{
                    role: "user",
                    content: messagesContent
                }],
            }, {
                headers: {
                    'Authorization': `Bearer ${OpenAIApiKey}`,
                },
            });

            const translatedValues = JSON.parse(response.data.choices[0].message.content);
            const updatedTranslations = {...translations};

            limitedUntranslatedKeys.forEach((key, index) => {
                if (translatedValues[limitedUntranslatedValues[index]]) {
                    updatedTranslations[key] = translatedValues[limitedUntranslatedValues[index]];
                }
            });

            setUpdatedKeys(prev => {
                const next = new Set(prev);
                limitedUntranslatedKeys.forEach(key => next.add(key));
                return next;
            });
            setTranslations(updatedTranslations);
            message.success('Generated 20 translations');
        } catch (error) {
            console.error(error);
            message.error('Failed to translate. Please try again.');
        } finally {
            setLoadingAi(false);
        }
    };

    const columns = useMemo(() => {
        const nextColumns = [];

        nextColumns.push({
            title: 'Key',
            dataIndex: 'key',
            key: 'translation-key',
            width: 240,
            ellipsis: true,
        });

        nextColumns.push({
            title: 'Translation',
            dataIndex: 'value',
            key: 'translation-value',
            render: (value, record) => (
                <TranslationInput
                    translationKey={record.key}
                    initialValue={value}
                    edited={updatedKeys.has(record.key)}
                    masterValue={isMasterLanguage ? null : (masterTranslation[record.key] || '')}
                    onUpdate={updateTranslation}
                />
            ),
        });

        if (!isMasterLanguage) {
            nextColumns.push({
                title: 'Estonian (master)',
                key: 'master-value',
                render: (_, record) => (
                    <MasterTranslationPreview
                        translationKey={record.key}
                        value={masterTranslation[record.key] || ''}
                    />
                ),
            });
        }

        if (isMasterLanguage) {
            nextColumns.push({
                title: '',
                key: 'actions',
                width: 48,
                align: 'center',
                render: (_, record) => (
                    <Popconfirm
                        title="Delete this translation?"
                        description={record.key}
                        onConfirm={() => removeTranslation(record.key)}
                        okText="Delete"
                        okButtonProps={{danger: true}}
                        cancelText="Cancel"
                    >
                        <Button
                            type="text"
                            size="small"
                            danger
                            icon={<DeleteOutlined/>}
                            aria-label="Delete translation"
                        />
                    </Popconfirm>
                ),
            });
        }

        return nextColumns;
    }, [isMasterLanguage, masterTranslation, updatedKeys, updateTranslation]);

    return (
        <Content style={{margin: '0 16px'}}>
            <Breadcrumb style={{margin: '16px 0'}}>
                <Breadcrumb.Item>Fitlap</Breadcrumb.Item>
                <Breadcrumb.Item><Link to="/translations">Translations</Link></Breadcrumb.Item>
                <Breadcrumb.Item>{params.id}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="site-layout-card">
                <h1><img style={{height: 30}} src={getImg()} alt=""/> Translations</h1>

                <Row type="flex" justify="space-between" align="middle" wrap>
                    <Space wrap>
                        <NewTranslation updateTranslation={updateTranslation}/>

                        <Upload beforeUpload={handleUpload} showUploadList={false} accept=".json">
                            <Button icon={<UploadOutlined/>}>Upload Translations</Button>
                        </Upload>
                    </Space>

                    <Space wrap>
                        <Select defaultValue="all" style={{width: 150}} onChange={setFilter}>
                            <Option value="all">Show all</Option>
                            <Option value="empty">Untranslated</Option>
                            <Option value="edited">Edited</Option>
                        </Select>

                        <Search placeholder="search" onSearch={onSearch} allowClear style={{width: 200}}/>
                        <Button onClick={saveTranslations} type="primary">Save</Button>
                    </Space>
                </Row>

                {!isMasterLanguage && (
                    <>
                        <br/>
                        <Row type="flex" justify="end">
                            <Button
                                loading={loadingAI}
                                onClick={translateUntranslated}
                                type="default"
                                icon={<StarOutlined/>}
                            >
                                <strong>AI</strong> - translate
                            </Button>
                        </Row>
                    </>
                )}

                <div style={{margin: '12px 0', color: '#8c8c8c'}}>
                    Showing {filteredRows.length.toLocaleString()} of {Object.keys(translations).length.toLocaleString()} translations
                    {updatedKeys.size > 0 && ` · ${updatedKeys.size.toLocaleString()} edited`}
                </div>

                <Table
                    className="translations-table"
                    rowKey="key"
                    size="small"
                    virtual
                    loading={loading}
                    pagination={false}
                    scroll={{y: tableHeight}}
                    dataSource={filteredRows}
                    columns={columns}
                />
            </div>
        </Content>
    );
};

export default TranslationView;
